import type { PartnerTag, PartnerTagCreate, PartnerTagUpdate } from '@shipium-corp/shipium-ts-types';
import type { Action, Thunk, Computed } from 'easy-peasy';
import { action, computed, thunk } from 'easy-peasy';
import type { ApplicationErrorOptions } from '../../../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type { Injections } from '../../services/injections';
import type { FetchAssetParams } from '../types';
import type { StoreModel } from '../..';

export interface TagModel {
  tagActionStart: Action<TagModel, void>;
  tagFailure: Action<TagModel, ApplicationErrorOptions>;
  lastAction?: number;
  activeTags: Computed<TagModel, PartnerTag[]>;
  tagCache: Record<string, PartnerTag>;
  addTagsToCache: Action<TagModel, PartnerTag[]>;
  fetchTagsByCriteria: Thunk<TagModel, FetchAssetParams | undefined, Injections, StoreModel, Promise<PartnerTag[]>>;
  createTag: Thunk<TagModel, PartnerTagCreate, Injections, StoreModel, Promise<PartnerTag>>;
  updateTag: Thunk<
    TagModel,
    { tagId: string; payload: PartnerTagUpdate },
    Injections,
    StoreModel,
    Promise<PartnerTag | void>
  >;
}

export const tagModel: TagModel = {
  activeTags: computed((state) => Object.values(state.tagCache).filter((t) => !!t.active && !t.archived) ?? []),
  tagCache: {},
  tagActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  tagFailure: action((state, error) => {
    console.error(error);
  }),
  fetchTagsByCriteria: thunk(async (actions, payload, { injections }) => {
    try {
      actions.tagActionStart();
      const tags = await injections.assetService.fetchTags(payload);
      actions.addTagsToCache(tags);
      return tags;
    } catch (error) {
      actions.tagFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching tags from the server.',
        asNotification: true,
      });
      return [];
    }
  }),
  createTag: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.tagActionStart();
      const tag = await injections.assetService.createTag(payload);
      actions.addTagsToCache([tag]);

      // Invalidate tag queries
      getStoreActions().queryInvalidation.invalidateQueries({ mutationType: 'tag-create', entityId: tag.tagId });

      return tag;
    } catch (error) {
      actions.tagFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'A server error occured, failed to create tag.',
        asNotification: true,
      });
      return error;
    }
  }),
  updateTag: thunk(async (actions, payload, { injections, getStoreActions }) => {
    try {
      actions.tagActionStart();
      const tag = await injections.assetService.updateTag({ tagId: payload.tagId, payload: payload.payload });
      actions.addTagsToCache([tag]);

      // Invalidate tag queries
      getStoreActions().queryInvalidation.invalidateQueries({
        mutationType: 'tag-update',
        entityId: payload.tagId,
      });

      return tag;
    } catch (error) {
      actions.tagFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'A server error occured, failed to update tag.',
        asNotification: true,
      });
    }
  }),
  addTagsToCache: action((state, payload) => {
    payload.reduce<Record<string, PartnerTag>>((prev, curr) => {
      prev[curr.tagId!] = curr;
      return prev;
    }, state.tagCache);
  }),
};
